import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import MainLayout from '../../components/layouts/MainLayout';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Avatar,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TablePagination,
  CircularProgress,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BlockIcon from '@mui/icons-material/Block';
import BusinessIcon from '@mui/icons-material/Business';
import { CONTEXTS, CONTROLLERS, SERVICES } from 'figshelf-modules';

const TABS = [
  { key: 'approved', label: 'Approved' },
  { key: 'pending', label: 'Pending' },
  { key: 'rejected', label: 'Rejected' },
];

const CompanyManagement = () => {
  const api = useContext(CONTEXTS.APIContext);
  const auth = useContext(CONTEXTS.AuthContext);
  const companyController = useMemo(() => new CONTROLLERS.CompanyController(api), [api]);

  const [tabIndex, setTabIndex] = useState(0);

  const [nameFilter, setNameFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const [selectedCompany, setSelectedCompany] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);

  const currentTab = TABS[tabIndex]?.key;

  const resetToFirstPage = () => setPage(0);

  const getStatusColor = (status) => {
    const v = String(status || '').toLowerCase();
    if (v === 'approved') return 'success';
    if (v === 'pending') return 'warning';
    if (v === 'rejected') return 'error';
    return 'default';
  };

  const fetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      setErrorMessage('');

      const parseListResponse = (res) => {
        const payload = res?.data;
        const rows = payload?.data?.data ?? payload?.data ?? [];
        const meta = payload?.meta ?? payload?.data?.meta;
        const total = meta?.totalItems ?? meta?.total ?? 0;
        return {
          rows: Array.isArray(rows) ? rows : [],
          totalItems: Number.isFinite(total) ? total : 0,
        };
      };

      const cacheParams = {
        tab: currentTab,
        skip: page * rowsPerPage,
        take: rowsPerPage,
        name: nameFilter || undefined,
        email: emailFilter || undefined,
        type: typeFilter || undefined,
      };

      const cacheKey = SERVICES.makeAdminCacheKey({
        userId: auth?.user?.id,
        scope: 'companies',
        params: cacheParams,
      });

      const cached = await SERVICES.getCachedAdminList(cacheKey, { maxAgeMs: 2 * 60 * 1000 });
      if (Array.isArray(cached?.rows)) {
        setItems(cached.rows);
        setTotalItems(Number.isFinite(cached.totalItems) ? cached.totalItems : 0);
      }


      if (currentTab === 'approved') {
        const res = await companyController.getCompanies({
          skip: page * rowsPerPage,
          take: rowsPerPage,
          name: nameFilter || undefined,
          email: emailFilter || undefined,
          type: typeFilter || undefined,
          status: 'approved',
        });

        const parsed = parseListResponse(res);
        setItems(parsed.rows);
        setTotalItems(parsed.totalItems);

        await SERVICES.cacheAdminList(cacheKey, { rows: parsed.rows, totalItems: parsed.totalItems }, { scope: 'companies' });
        return;
      }

      const res = await companyController.getCompanyDraftsByStatus(currentTab, {
        skip: page * rowsPerPage,
        take: rowsPerPage,
        name: nameFilter || undefined,
        email: emailFilter || undefined,
        type: typeFilter || undefined,
      });

      const parsed = parseListResponse(res);
      setItems(parsed.rows);
      setTotalItems(parsed.totalItems);

      await SERVICES.cacheAdminList(cacheKey, { rows: parsed.rows, totalItems: parsed.totalItems }, { scope: 'companies' });
    } catch (e) {
      console.error('Failed to load companies:', e);
      setItems([]);
      setTotalItems(0);
      setErrorMessage(e?.response?.data?.message || e?.message || 'Failed to load companies');
    } finally {
      setLoading(false);
    }
  }, [companyController, auth?.user?.id, currentTab, page, rowsPerPage, nameFilter, emailFilter, typeFilter]);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  useEffect(() => {
    resetToFirstPage();
  }, [tabIndex]);

  const handleOpenView = (company) => {
    setSelectedCompany(company);
    setViewDialogOpen(true);
  };

  const handleApprove = async (companyId) => {
    try {
      await companyController.approveCompany(companyId);
      fetchCompanies();
    } catch (e) {
      console.error('Approve company failed:', e);
    }
  };

  const handleReject = async (companyId) => {
    try {
      await companyController.rejectCompany(companyId, { reason: 'Rejected by admin' });
      fetchCompanies();
    } catch (e) {
      console.error('Reject company failed:', e);
    }
  };

  return (
    <MainLayout>
      <Box>
        <Typography variant="h5" fontWeight={600} mb={1}>Company Management</Typography>
        <Typography variant="subtitle2" color="text.secondary" mb={3}>
          Browse and moderate user-created company entries
        </Typography>

        <Card sx={{ borderRadius: 3, mb: 2 }}>
          <CardContent>
            <Tabs
              value={tabIndex}
              onChange={(e, v) => setTabIndex(v)}
              variant="scrollable"
              scrollButtons="auto"
              sx={{ mb: 2 }}
            >
              {TABS.map((t) => (
                <Tab key={t.key} label={t.label} />
              ))}
            </Tabs>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 200px', gap: 2, mb: 2 }}>
              <TextField
                label="Name"
                value={nameFilter}
                onChange={(e) => { setNameFilter(e.target.value); resetToFirstPage(); }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />

              <TextField
                label="Email"
                value={emailFilter}
                onChange={(e) => { setEmailFilter(e.target.value); resetToFirstPage(); }}
                size="small"
              />

              <FormControl size="small">
                <InputLabel>Type</InputLabel>
                <Select
                  label="Type"
                  value={typeFilter}
                  onChange={(e) => { setTypeFilter(e.target.value); resetToFirstPage(); }}
                >
                  <MenuItem value=""><em>All</em></MenuItem>
                  <MenuItem value="manufacturer">Manufacturer</MenuItem>
                  <MenuItem value="brand">Brand</MenuItem>
                  <MenuItem value="distributor">Distributor</MenuItem>
                  <MenuItem value="both">Both</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <TableContainer component={Paper} sx={{ boxShadow: 'none' }}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Company</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created By</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading && items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                        <CircularProgress size={28} />
                      </TableCell>
                    </TableRow>
                  ) : items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                        <Typography variant="body2" color="text.secondary">
                          No companies found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    (items || []).map((c) => {
                      const status = c.status || currentTab;
                      const avatarSrc = c.currentAvatar || c.logoUrl || null;
                      return (
                        <TableRow key={c.id} hover>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                              <Avatar src={avatarSrc || undefined} sx={{ width: 36, height: 36 }}>
                                {!avatarSrc && <BusinessIcon fontSize="small" />}
                              </Avatar>
                              <Box>
                                <Typography variant="body2" fontWeight={700}>
                                  {c.name || 'Untitled'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {c.email || ''}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Chip label={c.type || '—'} size="small" variant="outlined" />
                          </TableCell>
                          <TableCell>
                            <Chip label={status} size="small" color={getStatusColor(status)} sx={{ textTransform: 'capitalize' }} />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">{c.createdBy?.username || '—'}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {c.createdAt ? new Date(c.createdAt).toLocaleDateString() : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <IconButton size="small" onClick={() => handleOpenView(c)}>
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                            {currentTab !== 'approved' && (
                              <>
                                <IconButton size="small" color="success" onClick={() => handleApprove(c.id)}>
                                  <CheckCircleIcon fontSize="small" />
                                </IconButton>
                                <IconButton size="small" color="error" onClick={() => handleReject(c.id)}>
                                  <BlockIcon fontSize="small" />
                                </IconButton>
                              </>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={totalItems}
              page={page}
              onPageChange={(e, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50]}
            />
          </CardContent>
        </Card>

        <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth>
          <DialogTitle>Company Details</DialogTitle>
          <DialogContent>
            {selectedCompany && (
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Name</Typography>
                  <Typography variant="body1" fontWeight={700}>{selectedCompany.name}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Email</Typography>
                  <Typography variant="body1">{selectedCompany.email || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Type</Typography>
                  <Typography variant="body1">{selectedCompany.type || '—'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Website</Typography>
                  <Typography variant="body1">{selectedCompany.website || '—'}</Typography>
                </Box>
                <Box style={{ gridColumn: '1 / -1' }}>
                  <Typography variant="caption" color="text.secondary">Description</Typography>
                  <Typography variant="body2">{selectedCompany.description || '—'}</Typography>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </MainLayout>
  );
};

export default CompanyManagement;
